/**
 * The single place every network call passes through.
 *
 * Before this existed each of the ~190 `fetch` call sites had its own headers,
 * its own error handling and — critically — no timeout, so a stalled mobile
 * connection could hang a request until the OS gave up. Staff work at stations
 * on patchy signal, so that is the normal case, not the edge case.
 */

/** Long enough for a slow 3G round trip, short enough that the UI can recover. */
export const DEFAULT_TIMEOUT_MS = 20_000

/** Shorter budget for calls that block first paint (session bootstrap). */
export const BOOT_TIMEOUT_MS = 12_000

export class HttpError extends Error {
  readonly status: number
  readonly payload: unknown

  constructor (status: number, payload: unknown, message?: string) {
    super(message || `Request failed with status ${status}`)
    this.name = 'HttpError'
    this.status = status
    this.payload = payload
  }

  /** Server-side envelope error text, when the API returned one. */
  get apiError (): string | undefined {
    const p = this.payload as { error?: unknown } | null
    return typeof p?.error === 'string' ? p.error : undefined
  }

  /** API error code, e.g. `ACCOUNT_INACTIVE`. */
  get code (): string | undefined {
    const p = this.payload as { code?: unknown } | null
    return typeof p?.code === 'string' ? p.code : undefined
  }
}

export class TimeoutError extends Error {
  readonly timeoutMs: number

  constructor (timeoutMs: number) {
    super(`Request timed out after ${timeoutMs}ms`)
    this.name = 'TimeoutError'
    this.timeoutMs = timeoutMs
  }
}

export interface RequestOptions extends Omit<RequestInit, 'signal'> {
  /** Abort budget in milliseconds. Defaults to `DEFAULT_TIMEOUT_MS`. */
  timeoutMs?: number
  /** Caller-owned signal; aborting it aborts the request alongside the timeout. */
  signal?: AbortSignal | null
}

/** True for the offline/DNS/TLS class of failure, where a retry may help. */
export function isNetworkError (err: unknown): boolean {
  return err instanceof TypeError || err instanceof TimeoutError
}

/**
 * Performs a JSON request with a hard timeout.
 *
 * Resolves with the parsed body on 2xx. Throws `TimeoutError` on expiry,
 * `HttpError` on a non-2xx (carrying status and parsed payload), and lets
 * genuine network failures surface as `TypeError`.
 */
export async function requestJson<T = unknown> (
  url: string,
  options: RequestOptions = {}
): Promise<T> {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, signal: callerSignal, ...init } = options

  const controller = new AbortController()
  let timedOut = false

  const timer = setTimeout(() => {
    timedOut = true
    controller.abort()
  }, timeoutMs)

  const onCallerAbort = () => controller.abort()
  callerSignal?.addEventListener('abort', onCallerAbort)

  try {
    const res = await fetch(url, { ...init, signal: controller.signal })

    // A body may be absent (204) or malformed (HTML error page from a proxy).
    const payload = await res.json().catch(() => null)

    if (!res.ok) {
      const message =
        (payload as { error?: string } | null)?.error || `Request failed with status ${res.status}`
      throw new HttpError(res.status, payload, message)
    }

    return payload as T
  } catch (err) {
    if (timedOut) throw new TimeoutError(timeoutMs)
    throw err
  } finally {
    clearTimeout(timer)
    callerSignal?.removeEventListener('abort', onCallerAbort)
  }
}

/**
 * Wraps a promise in a timeout without aborting the underlying work.
 * For SDK calls (Supabase) that expose no signal but can still hang.
 */
export async function withTimeout<T> (
  promise: Promise<T>,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_resolve, reject) => {
        timer = setTimeout(() => reject(new TimeoutError(timeoutMs)), timeoutMs)
      })
    ])
  } finally {
    if (timer) clearTimeout(timer)
  }
}
